import { useState, type FormEvent } from 'react';
import styled from 'styled-components';
import { Button } from '@shared/ui/Button';
import { Card } from '@shared/ui/Card';
import { Field, Input, Label, Select } from '@shared/ui/Input';
import { useCalcPenaltyMutation, useGetKeyRateQuery } from '@shared/api';

type Mode = 'art_395' | 'contractual';

const Form = styled.form`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: ${({ theme }) => theme.spacing(3)};
  align-items: end;
  margin-bottom: ${({ theme }) => theme.spacing(4)};
`;

const Note = styled.div`
  margin-bottom: ${({ theme }) => theme.spacing(4)};
  font-size: 13px;
  color: ${({ theme }) => theme.colors.text.secondary};
`;

const Result = styled.div`
  margin-top: ${({ theme }) => theme.spacing(4)};
  padding: ${({ theme }) => theme.spacing(4)};
  border-radius: ${({ theme }) => theme.radius.md};
  background: ${({ theme }) => theme.colors.surface};
  border: 1px solid ${({ theme }) => theme.colors.border};
`;

const Total = styled.div`
  font-size: 22px;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.primary};
  margin-bottom: ${({ theme }) => theme.spacing(2)};
`;

const Formula = styled.div`
  font-family: ${({ theme }) => theme.font.mono};
  font-size: 13px;
  color: ${({ theme }) => theme.colors.text.secondary};
  margin-bottom: ${({ theme }) => theme.spacing(2)};
  white-space: pre-line;
`;

const RateBanner = styled.div`
  display: flex;
  align-items: baseline;
  gap: ${({ theme }) => theme.spacing(2)};
  margin-bottom: ${({ theme }) => theme.spacing(4)};
  padding: ${({ theme }) => theme.spacing(2)} ${({ theme }) => theme.spacing(3)};
  border-radius: ${({ theme }) => theme.radius.md};
  background: ${({ theme }) => theme.colors.surface};
  border: 1px solid ${({ theme }) => theme.colors.border};
  font-size: 13px;
`;

const RateValue = styled.span`
  font-weight: 700;
  color: ${({ theme }) => theme.colors.primary};
`;

const RateMeta = styled.span`
  color: ${({ theme }) => theme.colors.text.muted};
`;

const Hint = styled.div`
  font-size: 12px;
  color: ${({ theme }) => theme.colors.text.muted};
`;

interface Props {
  /// Подсказка по умолчанию: если передан, форма стартует с этой суммой.
  defaultPrincipal?: number;
  /// `embedded` рендерит без обёрточного Card (для использования внутри модалок).
  embedded?: boolean;
}

export function PenaltyCalculator({ defaultPrincipal, embedded }: Props) {
  const [mode, setMode] = useState<Mode>('art_395');
  const [principal, setPrincipal] = useState<string>(
    defaultPrincipal ? String(defaultPrincipal) : '',
  );
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [rate, setRate] = useState('0.1');
  const [calc, { data, isLoading, error, reset }] = useCalcPenaltyMutation();
  const { data: keyRate } = useGetKeyRateQuery();
  const currentRate = keyRate?.current ?? null;

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    reset();
    await calc({
      mode,
      principal: Number(principal),
      from,
      to,
      rate_per_day_pct: mode === 'contractual' ? Number(rate) : undefined,
    });
  }

  const errMsg =
    error && 'data' in error && error.data && typeof error.data === 'object'
      ? JSON.stringify(error.data)
      : '';

  const content = (
    <>
      <Note>
        Расчёт процентов по ст.395 ГК РФ или договорной неустойки. По ст.395 применяется ключевая
        ставка ЦБ РФ, действовавшая в каждом периоде начисления. Окончательный расчёт перед
        отправкой контрагенту обязательно проверяется юристом.
      </Note>
      {mode === 'art_395' && currentRate && (
        <RateBanner>
          <span>Ключевая ставка ЦБ РФ:</span>
          <RateValue>{currentRate.rate_pct}%</RateValue>
          <RateMeta>
            действует с {new Date(currentRate.effective_date).toLocaleDateString('ru-RU')}
          </RateMeta>
        </RateBanner>
      )}
      <Form onSubmit={onSubmit}>
        <Field>
          <Label htmlFor="mode">Режим</Label>
          <Select id="mode" value={mode} onChange={(e) => setMode(e.target.value as Mode)}>
            <option value="art_395">Ст.395 ГК (ключевая ставка ЦБ)</option>
            <option value="contractual">Договорная неустойка</option>
          </Select>
        </Field>
        <Field>
          <Label htmlFor="principal">Сумма основного долга, ₽ *</Label>
          <Input
            id="principal"
            type="number"
            step="0.01"
            value={principal}
            onChange={(e) => setPrincipal(e.target.value)}
            required
          />
        </Field>
        <Field>
          <Label htmlFor="from">Период с *</Label>
          <Input
            id="from"
            type="date"
            value={from}
            onChange={(e) => setFrom(e.target.value)}
            required
          />
        </Field>
        <Field>
          <Label htmlFor="to">Период по *</Label>
          <Input id="to" type="date" value={to} onChange={(e) => setTo(e.target.value)} required />
        </Field>
        {mode === 'contractual' && (
          <Field>
            <Label htmlFor="rate">Ставка %/день *</Label>
            <Input
              id="rate"
              type="number"
              step="0.001"
              value={rate}
              onChange={(e) => setRate(e.target.value)}
              required
            />
          </Field>
        )}
        <Field style={{ alignSelf: 'end' }}>
          <Button type="submit" disabled={isLoading}>
            Рассчитать
          </Button>
        </Field>
      </Form>

      {errMsg && <Hint style={{ color: 'crimson' }}>Ошибка: {errMsg}</Hint>}

      {data && (
        <Result>
          <Total>
            {data.total.toLocaleString('ru-RU', {
              minimumFractionDigits: 2,
              maximumFractionDigits: 2,
            })}{' '}
            ₽
          </Total>
          <Formula>{data.formula}</Formula>
          <Hint>
            Дней: {data.days} · Ставка: {data.rate_used_pct}%{mode === 'contractual' ? '/день' : ''}
          </Hint>
          <Hint style={{ marginTop: 8 }}>{data.note}</Hint>
        </Result>
      )}
    </>
  );

  return embedded ? content : <Card>{content}</Card>;
}
